import { useRef, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { formaterDate, formaterIdentite } from '@gsm/partage';
import { useRessource, messageErreur } from '../api/ressource';
import { api, ErreurApi } from '../api/client';
import { useAuth } from '../auth/contexte';
import { Bloc, Tableau, type Colonne } from '../composants/Tableau';
import { BandeauAlerte } from '../composants/BandeauAlerte';
import { Champ } from '../composants/Champ';
import { Pastille } from '../composants/Pastille';
import { Modale } from '../composants/Modale';
import { Icone } from '../composants/Icone';
import { ModaleMandat } from '../composants/ModaleMandat';
import { LienFiche, LienPiece } from '../composants/LienAction';
import { LIBELLE_STATUT_MANDAT, TON_STATUT_MANDAT } from './Mandats';

interface Client {
  id: string;
  reference: string;
  civilite: string | null;
  nom: string;
  prenom: string;
  cin: string;
  dateNaissance: string;
  nationalite: string;
  adresse: string | null;
  ville: string | null;
  telephone: string | null;
  email: string | null;
  mandats: {
    id: string;
    reference: string;
    statut: string;
    profil: { libelle: string };
    agence: { code: string };
  }[];
  documents: {
    id: string;
    typeDoc: string;
    nomFichier: string;
    typeMime: string;
    tailleOctets: string;
    hashSha256: string;
    deposeLe: string;
    retireLe: string | null;
    motifRetrait: string | null;
  }[];
}

export function FicheClient() {
  const { id } = useParams<{ id: string }>();
  const { aRole } = useAuth();
  const { donnees, chargement, erreur, recharger } = useRessource<Client>(`/clients/${id}`);
  const [ouverte, setOuverte] = useState(false);

  const fichierRef = useRef<HTMLInputElement>(null);
  const [typeDoc, setTypeDoc] = useState('MANDAT_SIGNE');
  const [depotEnCours, setDepotEnCours] = useState(false);
  const [erreurDepot, setErreurDepot] = useState<string | null>(null);

  const [retrait, setRetrait] = useState<{ id: string; nom: string } | null>(null);
  const [motifRetrait, setMotifRetrait] = useState('');
  const [retraitEnCours, setRetraitEnCours] = useState(false);
  const [erreurRetrait, setErreurRetrait] = useState<string | null>(null);

  const client = donnees;
  const peutSaisir = aRole('MIDDLE_BO', 'RESP_GESTION', 'ADMIN');
  const mandatVivant = client?.mandats.find((m) => !['CLOTURE', 'ANNULE'].includes(m.statut));

  async function deposer() {
    const fichier = fichierRef.current?.files?.[0];
    if (!fichier) return;
    setErreurDepot(null);
    setDepotEnCours(true);
    try {
      const formulaire = new FormData();
      formulaire.append('fichier', fichier);
      formulaire.append('clientId', String(id));
      // Rattaché au mandat vivant quand il en existe un : sans cela, la pièce
      // resterait invisible pour la RG-03 et le mandat serait réputé sans
      // mandat signé au moment de l'activation.
      if (mandatVivant) formulaire.append('mandatId', mandatVivant.id);
      formulaire.append('typeDoc', typeDoc);
      await api.deposer('/documents', formulaire);
      if (fichierRef.current) fichierRef.current.value = '';
      await recharger();
    } catch (e) {
      setErreurDepot(e instanceof ErreurApi ? messageErreur(e) : 'Dépôt impossible.');
    } finally {
      setDepotEnCours(false);
    }
  }

  function ouvrirRetrait(idPiece: string, nom: string) {
    setRetrait({ id: idPiece, nom });
    setMotifRetrait('');
    setErreurRetrait(null);
  }

  async function confirmerRetrait(evenement: FormEvent) {
    evenement.preventDefault();
    if (!retrait) return;
    setErreurRetrait(null);
    setRetraitEnCours(true);
    try {
      await api.post(`/documents/${retrait.id}/retirer`, { motif: motifRetrait });
      setRetrait(null);
      await recharger();
    } catch (e) {
      setErreurRetrait(e instanceof ErreurApi ? messageErreur(e) : 'Retrait impossible.');
    } finally {
      setRetraitEnCours(false);
    }
  }

  if (chargement) return <p className="surtitre">Chargement…</p>;
  if (erreur || !client)
    return (
      <BandeauAlerte nature="refus" titre="Client introuvable">
        {messageErreur(erreur) ?? 'Cette fiche n’existe pas.'}
      </BandeauAlerte>
    );

  const colonnesDocuments: Colonne<Client['documents'][number]>[] = [
    { cle: 'type', entete: 'Type', rendu: (d) => d.typeDoc },
    {
      cle: 'nom',
      entete: 'Fichier',
      rendu: (d) => (
        <LienPiece href={`/api/v1/documents/${d.id}/contenu`}>{d.nomFichier}</LienPiece>
      ),
    },
    { cle: 'mime', entete: 'Format', rendu: (d) => d.typeMime },
    {
      cle: 'taille',
      entete: 'Taille',
      num: true,
      rendu: (d) => `${Math.round(Number(d.tailleOctets) / 1024)} Ko`,
    },
    {
      cle: 'hash',
      entete: 'Empreinte SHA-256',
      num: true,
      rendu: (d) => d.hashSha256.slice(0, 16) + '…',
    },
    { cle: 'depot', entete: 'Déposé le', num: true, rendu: (d) => formaterDate(d.deposeLe) },
    {
      cle: 'etat',
      entete: 'État',
      // La pièce retirée reste affichée avec son motif : la correction se lit
      // sur place, comme une contre-passation, plutôt que de disparaître.
      rendu: (d) =>
        d.retireLe ? (
          <span className="etiquette-mention">
            <Pastille ton="attente">Retirée</Pastille>
            <span className="consigne">{d.motifRetrait ?? '—'}</span>
          </span>
        ) : peutSaisir ? (
          <button
            type="button"
            className="tableau__action"
            onClick={() => ouvrirRetrait(d.id, d.nomFichier)}
            aria-label={`Retirer ${d.nomFichier}`}
          >
            <Icone nom="fermer" taille={14} />
          </button>
        ) : (
          '—'
        ),
    },
  ];

  return (
    <>
      <p className="entete__fil">
        <Link to="/clients">Clients</Link> › {client.reference}
      </p>
      <div className="entete">
        <h1>{formaterIdentite(client.nom, client.prenom)}</h1>
        <span className="entete__ref">{client.reference}</span>
        {mandatVivant && (
          <Pastille ton={TON_STATUT_MANDAT[mandatVivant.statut] ?? 'neutre'}>
            {LIBELLE_STATUT_MANDAT[mandatVivant.statut] ?? mandatVivant.statut}
          </Pastille>
        )}
      </div>

      <section className="cles" aria-label="Identité">
        <div className="cle">
          <span className="surtitre">CIN</span>
          <div className="cle__valeur">{client.cin}</div>
        </div>
        <div className="cle">
          <span className="surtitre">Naissance</span>
          <div className="cle__valeur">{formaterDate(client.dateNaissance)}</div>
        </div>
        <div className="cle">
          <span className="surtitre">Ville</span>
          <div className="cle__valeur">{client.ville ?? '—'}</div>
        </div>
        <div className="cle">
          <span className="surtitre">Téléphone</span>
          <div className="cle__valeur">{client.telephone ?? '—'}</div>
        </div>
      </section>

      <Bloc
        titre="Mandats"
        meta={`${client.mandats.length}`}
        actions={
          peutSaisir &&
          (mandatVivant ? (
            // RG-01 : un seul mandat vivant par client. Le bouton disparaissait
            // sans un mot ; on dit pourquoi plutôt que de laisser chercher.
            <span className="consigne">
              Mandat {mandatVivant.reference} en cours — un client n’en détient qu’un à la fois.
            </span>
          ) : (
            <button
              type="button"
              className="bouton bouton--icone bouton--compact"
              onClick={() => setOuverte(true)}
            >
              <Icone nom="creer" taille={15} />
              Enregistrer un mandat
            </button>
          ))
        }
      >
        <Tableau
          colonnes={[
            {
              cle: 'reference',
              entete: 'Référence',
              num: true,
              rendu: (m) => <LienFiche to={`/mandats/${m.id}`}>{m.reference}</LienFiche>,
            },
            { cle: 'profil', entete: 'Profil', rendu: (m) => m.profil.libelle },
            { cle: 'agence', entete: 'Agence', rendu: (m) => m.agence.code },
            {
              cle: 'statut',
              entete: 'Statut',
              rendu: (m) => (
                <Pastille ton={TON_STATUT_MANDAT[m.statut] ?? 'neutre'}>
                  {LIBELLE_STATUT_MANDAT[m.statut] ?? m.statut}
                </Pastille>
              ),
            },
          ]}
          lignes={client.mandats}
          cle={(m) => m.id}
          vide="Aucun mandat enregistré."
        />
      </Bloc>

      <Bloc titre="Pièces déposées" meta="mandat signé et RIB">
        <Tableau
          colonnes={colonnesDocuments}
          lignes={client.documents}
          cle={(d) => d.id}
          vide="Aucune pièce déposée."
        />

        {peutSaisir && (
          <>
            {erreurDepot && (
              <div className="espace-avant">
                <BandeauAlerte nature="refus" titre="Dépôt refusé">
                  {erreurDepot}
                </BandeauAlerte>
              </div>
            )}
            <div className="barre-actions barre-actions--apres barre-actions--champs">
              <div className="champ">
                <label className="surtitre" htmlFor="typedoc-client">
                  Type
                </label>
                <select
                  id="typedoc-client"
                  className="champ__select"
                  value={typeDoc}
                  onChange={(e) => setTypeDoc(e.target.value)}
                >
                  <option value="MANDAT_SIGNE">Mandat signé</option>
                  <option value="RIB">RIB</option>
                </select>
              </div>
              <input ref={fichierRef} type="file" accept=".pdf,.jpg,.jpeg,.png" />
              <button className="bouton" disabled={depotEnCours} onClick={() => void deposer()}>
                {depotEnCours ? 'Dépôt…' : 'Déposer'}
              </button>
            </div>
            <p className="consigne consigne--pied">
              {mandatVivant
                ? `PDF, JPEG ou PNG. La pièce est rattachée au mandat ${mandatVivant.reference}.`
                : 'PDF, JPEG ou PNG. La pièce est rattachée au client ; elle le restera après création du mandat.'}
            </p>
          </>
        )}
      </Bloc>

      <ModaleMandat
        ouverte={ouverte}
        onFermer={() => setOuverte(false)}
        client={client}
        onCree={async () => {
          setOuverte(false);
          await recharger();
        }}
      />

      <Modale
        ouverte={retrait !== null}
        onFermer={() => setRetrait(null)}
        surtitre={retrait?.nom}
        titre="Retirer cette pièce"
      >
        {erreurRetrait && (
          <BandeauAlerte nature="refus" titre="Retrait refusé">
            {erreurRetrait}
          </BandeauAlerte>
        )}
        <p className="consigne consigne--tete">
          La pièce n’est pas supprimée : le fichier et son empreinte restent conservés, et le
          retrait est journalisé. Elle cesse simplement de compter — un mandat signé retiré ne
          satisfait plus la RG-03.
        </p>
        <form onSubmit={(e) => void confirmerRetrait(e)} noValidate>
          <Champ
            label="Motif du retrait"
            value={motifRetrait}
            onChange={(e) => setMotifRetrait(e.target.value)}
            aide="Obligatoire — c’est lui qui rendra le geste relisible."
            required
          />
          <div className="modale__actions">
            <button
              type="button"
              className="bouton bouton--fantome"
              onClick={() => setRetrait(null)}
            >
              Annuler
            </button>
            <button
              className="bouton"
              type="submit"
              disabled={retraitEnCours || !motifRetrait.trim()}
            >
              {retraitEnCours ? 'Retrait…' : 'Retirer la pièce'}
            </button>
          </div>
        </form>
      </Modale>
    </>
  );
}
